/**
 * The transfer details window and the A4 voucher (C23, B4 — D05 §3, §5 "Voucher"; old CashTransfers.tsx:2188-2366,
 * print reference p08_transfer_voucher). Reads `GET /transfers/:id` (the identity document decrypted for this view only,
 * ADR-03 §2.7) and shows the voucher template of `@faylasuf/print` with the office's name (D-052, D-125), the server's
 * voucher number (D-147) and the timestamp in the office time zone (D-064). Printing first records the issue
 * (`POST /transfers/:id/print`, audited; a reprint is a copy, D-147), then prints the same template through the print
 * helper (no iframe / document.write, ADR-03 §2.4). [C35] The window switches the copy between A4 and 80 mm (D-149),
 * offers the server PDF (D-146), prints the words of the total on 80 mm (D-148) and leaves the commission out when the
 * office hides profit on customer documents (D-148).
 */
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { TransferVoucherA4, Voucher80, transferVoucher80, type TransferVoucherA4Props, type Voucher80Props } from "@faylasuf/print";
import { TransferReceiptModal } from "@faylasuf/ui";
import type { TransferDetail, TransfersSummary } from "../../api/types.js";
import { useApp, useSession } from "../../app/context.js";
import { errorMessage, isSessionLost } from "../../app/errors.js";
import { keys } from "../../app/query.js";
import { useToast } from "../../app/toast.js";
import { useDocuments, usePrintJob, usePrintSettings, useVoucherFormat } from "../../app/documents.js";
import { usePrintOffice } from "../../app/printOffice.js";
import { printedNow } from "../customers/EntryVoucherContainer.js";
import { amountWith, money2 } from "./format.js";

export function ReceiptContainer(props: { readonly id: string; readonly data: TransfersSummary; readonly autoPrint: boolean; readonly onClose: () => void }) {
  const { t, source, locale } = useApp();
  const { session, sessionLost } = useSession();
  const toast = useToast();
  const office = usePrintOffice();
  const settings = usePrintSettings();
  const docs = useDocuments();
  const job = usePrintJob();
  const [format, setFormat, copyIssued] = useVoucherFormat("transfer_voucher"); // [C36] T-18: the switch is for one copy
  const q = useQuery({ queryKey: keys.transferDetail(props.id), queryFn: () => source.transferDetail(props.id) });
  const [autoDone, setAutoDone] = useState(false);
  const [words, setWords] = useState<string[]>([]);
  const d = q.data;

  useEffect(() => {
    if (d) void docs.words([{ lines: [{ amount: d.total, currency: d.currency }] }]).then((w) => setWords(w.filter((x) => x !== "")));
  }, [d?.total, d?.currency]);

  const base = d ? voucherOf(d, props.data, t, locale, office.name, session.office.timeZone) : null;
  // [C35] D-148: the office may hide profit and commission on customer documents
  const voucherProps = base ? { ...base, office, words, ...(settings.showProfit ? {} : { commissionLabel: null }) } : null;
  const docOf = (copy: boolean) => {
    const p = { ...voucherProps!, copy };
    return format === "80mm" ? { template: "voucher_80mm" as const, props: transferVoucher80(p, office, printedNow(session.office.timeZone, locale)) } : { template: "transfer_voucher_a4" as const, props: p };
  };
  const issue = async (channel: "print" | "pdf") => {
    try {
      return await source.transferPrinted(props.id, channel);
    } catch (e) {
      if (isSessionLost(e)) sessionLost();
      else toast(errorMessage(t, e), "error");
      return null;
    }
  };
  const print = async () => {
    const r = await issue("print");
    if (!r || !voucherProps) return;
    const x = docOf(r.copy);
    job.start(x.template === "voucher_80mm" ? <Voucher80 {...(x.props as Voucher80Props)} id="transfer-voucher-print" /> : <TransferVoucherA4 {...(x.props as TransferVoucherA4Props)} id="transfer-voucher-print" />, "transfer-voucher-print", format);
    copyIssued();
  };
  const pdf = async () => {
    const r = await issue("pdf");
    if (!r || !voucherProps || !d) return;
    const x = docOf(r.copy);
    copyIssued();
    await docs.downloadPdf({ template: x.template, props: x.props as unknown as Record<string, unknown>, fileName: `transfer-${d.code}`, docType: "transfer_voucher", ...(d.voucherNumber ? { number: String(d.voucherNumber) } : {}) });
  };

  useEffect(() => {
    if (props.autoPrint && d && d.status === "posted" && !autoDone) {
      setAutoDone(true);
      void print();
    }
  }, [props.autoPrint, d, autoDone]);

  const statusNote = !d
    ? null
    : d.status === "cancelled" ? t("ct.status_note_cancelled", { reason: d.cancel?.reason ?? "" })
    : d.status === "rejected" ? t("ct.status_note_rejected", { reason: d.rejectReason ?? "" })
    : d.status === "withdrawn" ? t("ct.status_note_withdrawn")
    : d.status === "pending" ? t("ct.status_note_pending")
    : null;
  const preview = voucherProps ? docOf(d!.prints > 0) : null;

  return (
    <>
      <TransferReceiptModal
        open={true}
        voucher={
          !preview ? <div className="p-16 text-center text-gray-400 font-bold text-xs">…</div>
          : preview.template === "voucher_80mm" ? <div className="py-2 flex justify-center"><Voucher80 {...(preview.props as Voucher80Props)} /></div>
          : <TransferVoucherA4 {...(preview.props as TransferVoucherA4Props)} />
        }
        statusNote={statusNote}
        canPrint={d?.status === "posted"}
        onPrint={() => void print()}
        onClose={props.onClose}
        format={format}
        onFormatChange={setFormat}
        onPdf={() => void pdf()}
      />
      {job.portal}
    </>
  );
}

export function voucherOf(d: TransferDetail, data: TransfersSummary, t: (k: string, p?: Record<string, string | number>) => string, locale: "ar" | "en", officeName: string, timeZone: string): Omit<TransferVoucherA4Props, "copy"> {
  const cur = data.currencies;
  const paymentLabel = d.paymentKind === "cash" ? t("ct.pay_cash") : d.paymentKind === "credit" ? t("ct.pay_credit") : t("ct.pay_trust");
  const q = cur.find((c) => c.code === d.currency)?.quoteUnit ?? "1";
  const derived = d.derived;
  let dateLabel = d.createdAt;
  try {
    dateLabel = new Date(d.createdAt).toLocaleString(locale === "ar" ? "ar-IQ" : "en-US", { timeZone });
  } catch {
    /* keep ISO */
  }
  return {
    locale,
    officeName,
    voucherNumber: d.voucherNumber,
    code: d.code,
    beneficiaryName: d.beneficiaryName,
    documentId: d.documentId,
    direction: d.direction === "out" ? "sent" : "received",
    destination: d.destination,
    route: d.route === "partner" ? "office" : "vault",
    paymentLabel,
    partnerName: d.partner?.name ?? null,
    customer: d.customer ? { name: d.customer.name, kind: d.paymentKind === "trust" ? "trust" : "credit" } : null,
    principalLabel: amountWith(d.amount, d.currency, cur),
    commissionLabel: amountWith(d.commission, d.currency, cur),
    partnerFeeLabel: d.route === "partner" ? amountWith(d.partnerFee, d.currency, cur) : null,
    partnerFeeIncoming: d.route === "partner" && d.direction === "in",
    totalLabel: amountWith(d.total, d.currency, cur),
    paidToBeneficiaryLabel: d.direction === "in" && d.method !== "settled" ? amountWith(derived["beneficiary_gets"] ?? d.amount, d.currency, cur) : null,
    settlement: d.settlement
      ? { cashLabel: `${money2(d.settlement.total)} ${d.settlement.currency === "IQD" ? "د.ع" : "$"}`, rateLabel: d.settlement.rate ? `${q} ${d.currency} = ${money2(d.settlement.rate)} ${d.settlement.currency === "IQD" ? "د.ع" : "$"}` : null }
      : null,
    operatorName: d.operator,
    dateLabel,
  };
}

